import { db } from "@/_firebase/firebaseConfig";
import {
  DimensionDataType,
  shortFormToLongFormDimensionSlugMap,
} from "@/assets/traitInfo/dimensionData";
import {
  ConnectionAvailability,
  DimensionEvent,
  DimensionEventStatus,
  PublicProfile,
} from "@dimensional-engineering/dimensional-models";
import { collection, getDocs } from "firebase/firestore";

import { checkConnectionDimensionDiscoveryGate } from "../Compatibility/utils";

export function getRequiredConnectionLevel(
  connectionAvailabilities: ConnectionAvailability[],
  connectionLevel:
    | "connected"
    | "close"
    | "deep"
    | "sent"
    | "unconnected"
    | "received"
    | null
): ConnectionAvailability | null {
  //null means permission required connection is active.

  if (connectionLevel === "unconnected") {
    return ConnectionAvailability.basic;
  }

  if (connectionAvailabilities.includes(ConnectionAvailability.private)) {
    return ConnectionAvailability.private;
  }

  if (connectionAvailabilities.includes(ConnectionAvailability.unconnected)) {
    return ConnectionAvailability.unconnected;
  }

  if (connectionAvailabilities.includes(ConnectionAvailability.basic)) {
    if (
      connectionLevel !== "deep" &&
      connectionLevel !== "close" &&
      connectionLevel !== "connected"
    ) {
      return ConnectionAvailability.basic;
    }
    if (
      connectionLevel !== "deep" &&
      connectionLevel !== "close" &&
      connectionLevel === "connected"
    ) {
      return null;
    }
  }

  if (connectionAvailabilities.includes(ConnectionAvailability.close)) {
    if (connectionLevel !== "deep" && connectionLevel !== "close") {
      return ConnectionAvailability.close;
    }
    if (connectionLevel !== "deep" && connectionLevel === "close") {
      return null;
    }
  }

  if (
    connectionAvailabilities.includes(ConnectionAvailability.deep) &&
    connectionLevel !== "deep"
  ) {
    return ConnectionAvailability.deep;
  }

  return null;
}

export function hasCompletedRequiredDimensions(
  requiredDimensionSlugs: string[],
  userDimensionEvents: DimensionEvent[] | null
): boolean {
  //finishedSlugs is just for debugging
  const finishedSlugs: string[] = [];
  const notFinishedSlugs: string[] = [];

  requiredDimensionSlugs.forEach((slug) => {
    const foundDimensionEvent = userDimensionEvents?.find(
      (e) => e.dimensionSlug === slug
    );
    if (!foundDimensionEvent) {
      notFinishedSlugs.push(slug);
    } else {
      //has dimension event
      if (
        foundDimensionEvent.event === DimensionEventStatus.deferred ||
        foundDimensionEvent.event === DimensionEventStatus.deferredGated
      ) {
        //has long form so we have to look for it
        const longFormFoundDimensionEvent = userDimensionEvents?.find(
          (e) => e.dimensionSlug === shortFormToLongFormDimensionSlugMap[slug]
        );

        if (!longFormFoundDimensionEvent) {
          // long form dim event not created for some reason
          finishedSlugs.push(slug);
        } else if (
          longFormFoundDimensionEvent.event === DimensionEventStatus.complete ||
          longFormFoundDimensionEvent.event === DimensionEventStatus.reset ||
          longFormFoundDimensionEvent.event === DimensionEventStatus.resetGated
        ) {
          //long form has been completed at least once
          if (
            longFormFoundDimensionEvent.event !==
            DimensionEventStatus.resetGated
          ) {
            //long form not gated
            finishedSlugs.push(slug);
          } else {
            //long form gated
            notFinishedSlugs.push(slug);
          }
        } else {
          //long form is not completed once yet. In intermittent state
          //between finish of short form and start of long form
          if (foundDimensionEvent.event === DimensionEventStatus.deferred) {
            finishedSlugs.push(slug);
          } else {
            notFinishedSlugs.push(slug);
          }
        }
      } else {
        //doesn't have long form
        if (
          foundDimensionEvent.event === DimensionEventStatus.complete ||
          foundDimensionEvent.event === DimensionEventStatus.reset
        ) {
          //not gated
          finishedSlugs.push(slug);
        } else {
          //gated
          notFinishedSlugs.push(slug);
        }
      }
    }
  });
  if (notFinishedSlugs.length === 0) {
    return true;
  }
  return false;
}

export function listUserCompletedDimensionsForGating(
  userDimensionEvents: DimensionEvent[] | null | undefined
): string[] {
  const finishedSlugs: string[] = [];

  if (!userDimensionEvents) {
    return [];
  } else {
    userDimensionEvents.forEach((dim) => {
      if (
        dim.event === DimensionEventStatus.deferred ||
        dim.event === DimensionEventStatus.deferredGated
      ) {
        const longFormFoundDimensionEvent = userDimensionEvents?.find(
          (e) =>
            e.dimensionSlug ===
            shortFormToLongFormDimensionSlugMap[dim.dimensionSlug]
        );
        if (
          longFormFoundDimensionEvent?.event ===
            DimensionEventStatus.complete ||
          longFormFoundDimensionEvent?.event === DimensionEventStatus.reset ||
          longFormFoundDimensionEvent?.event === DimensionEventStatus.resetGated
        ) {
          //long form has been completed at least once
          if (
            longFormFoundDimensionEvent.event !==
            DimensionEventStatus.resetGated
          ) {
            //long form not gated
            finishedSlugs.push(dim.dimensionSlug);
          }
        } else {
          //long form is not completed once yet. In intermittent state
          //between finish of long form and start of short form
          if (dim.event === DimensionEventStatus.deferred) {
            finishedSlugs.push(dim.dimensionSlug);
          }
        }
      } else {
        if (
          dim.event === DimensionEventStatus.complete ||
          dim.event === DimensionEventStatus.reset
        ) {
          //not gated
          finishedSlugs.push(dim.dimensionSlug);
        }
      }
    });
  }

  return finishedSlugs;
}

export function listUserCompletedDimensionsNonGating(
  userDimensionEvents: DimensionEvent[] | null | undefined
): string[] {
  const finishedSlugs: string[] = [];

  if (!userDimensionEvents) {
    return [];
  } else {
    userDimensionEvents.forEach((dim) => {
      if (
        dim.event === DimensionEventStatus.deferred ||
        dim.event === DimensionEventStatus.deferredGated
      ) {
        //check for long form
        const longFormFoundDimensionEvent = userDimensionEvents?.find(
          (e) =>
            e.dimensionSlug ===
            shortFormToLongFormDimensionSlugMap[dim.dimensionSlug]
        );
        if (
          longFormFoundDimensionEvent?.event === DimensionEventStatus.complete
        ) {
          //long form has been completed
          finishedSlugs.push(dim.dimensionSlug);
        }
      } else {
        //no long form version
        if (dim.event === DimensionEventStatus.complete) {
          finishedSlugs.push(dim.dimensionSlug);
        }
      }
    });
  }

  return finishedSlugs;
}

export function generateDimensionButtonStatus(
  dimension: DimensionDataType,
  dimensionEvents: DimensionEvent[] | null
) {
  const longformSLug = dimension.longFormSlug;
  const completedSlugs = listUserCompletedDimensionsNonGating(dimensionEvents);
  const foundShortFormEvent = dimensionEvents?.find(
    (e) => e.dimensionSlug == dimension.slug
  );
  const foundLongFormEvent = dimensionEvents?.find(
    (e) => e.dimensionSlug == dimension.longFormSlug
  );

  if (longformSLug) {
    if (completedSlugs.includes(longformSLug)) {
      //done long form
      return "View";
    } else {
      ///long form not done
      //check for short form to see if done
      //if done, check for long form status
      //else check for short form status
      if (!foundShortFormEvent) {
        //not started short form
        return "Start";
      }
      if (foundShortFormEvent?.event === DimensionEventStatus.started) {
        //in the middle of short form
        return "Continue";
      }
      if (!foundLongFormEvent) {
        //Done short form but not started long form
        return "View";
      } else {
        //has long form event
        if (foundLongFormEvent.event === DimensionEventStatus.complete) {
          return "View";
        }
        return "Continue";
      }
    }
  } else {
    if (!foundShortFormEvent) {
      //not started
      return "Start";
    }
    if (
      foundShortFormEvent?.event === DimensionEventStatus.started ||
      foundShortFormEvent?.event === DimensionEventStatus.reset ||
      foundShortFormEvent?.event === DimensionEventStatus.resetGated
    ) {
      //in the middle of short form
      return "Continue";
    }
    return "View";
  }
}

export function hasUserCompletedCorePlusShortForms(
  userDimensionEvents: DimensionEvent[] | null | undefined
): boolean {
  let finished: boolean = true;
  const requiredSlugs = [
    "personality",
    "personality-II",
    "values",
    "strengths",
    "secondary-traits",
  ];
  requiredSlugs.forEach((s) => {
    const foundEvent = userDimensionEvents?.find((e) => e.dimensionSlug === s);
    if (!foundEvent) {
      finished = false;
    } else {
      if (foundEvent.event === DimensionEventStatus.started) {
        finished = false;
      }
    }
  });
  return finished;
}

export async function checkConnectionDimensionCompletion(
  publicProfile: PublicProfile | null | undefined,
  requiredDimensionSlugs: string[]
): Promise<null | "Nudge view"> {
  let dimensionEventRef = collection(
    db,
    `members/${publicProfile?.ownerUUID}/dimensionEvents`
  );
  return await getDocs(dimensionEventRef).then((querySnap) => {
    const dimensionEvents: DimensionEvent[] = [];

    querySnap.forEach((doc) => {
      const d = doc.data() as DimensionEvent;
      dimensionEvents.push(d);
    });

    const finishedDimensionSlugs =
      listUserCompletedDimensionsForGating(dimensionEvents);

    const connectionCompletedAllDimentsions =
      checkConnectionDimensionDiscoveryGate(
        finishedDimensionSlugs,
        requiredDimensionSlugs
      );

    if (!connectionCompletedAllDimentsions) {
      return "Nudge view";
    } else {
      return null;
    }
  });
}
